/**
 * Pure helpers for post-game analysis (client Stockfish WASM or server cloud payload).
 */

export const ANALYSIS_PRESETS = {
  quick: { depth: 10, multipv: 3, labelKey: 'playing.analysisQuick' },
  deep: { depth: 16, multipv: 4, labelKey: 'playing.analysisDeep' },
  cloud: { depth: 14, multipv: 3, labelKey: 'playing.analysisCloud' },
};

export function evalCp(ev) {
  if (!ev) return 0;
  if (ev.mate != null) return ev.mate > 0 ? 10000 : -10000;
  return ev.scoreCp ?? 0;
}

/** Convert side-to-move POV eval to White POV. */
export function toWhiteEval(ev, sideToMove) {
  if (!ev) return null;
  const flip = sideToMove === 'b';
  const flipScore = (scoreCp, mate) => {
    let s = scoreCp;
    let m = mate;
    if (flip) {
      if (s != null) s = -s;
      if (m != null) m = -m;
    }
    return { scoreCp: s, mate: m };
  };
  const top = flipScore(ev.scoreCp, ev.mate);
  const lines = (ev.lines || []).map((ln) => {
    const f = flipScore(ln.scoreCp, ln.mate);
    return { ...ln, scoreCp: f.scoreCp, mate: f.mate };
  });
  return {
    bestMove: ev.bestMove,
    scoreCp: top.scoreCp,
    mate: top.mate,
    lines,
  };
}

export function classifyMove(before, after, moverIsWhite) {
  const beforeCp = evalCp(before);
  const afterCp = evalCp(after);
  const loss = moverIsWhite ? beforeCp - afterCp : afterCp - beforeCp;
  const gain = -loss;
  let quality = 'best';
  // Brilliant: large swing toward the mover that finds a forced win / big advantage from near-equal
  if (gain >= 200 && Math.abs(beforeCp) < 150) {
    quality = 'brilliant';
  } else if (loss > 200) quality = 'blunder';
  else if (loss > 100) quality = 'mistake';
  else if (loss > 50) quality = 'inaccuracy';
  else if (loss > 20) quality = 'good';
  else if (loss <= 0 && Math.abs(gain) < 15) quality = 'best';
  const points = Math.max(0, Math.min(100, 100 - Math.max(0, loss) / 2));
  return { quality, loss: Math.round(loss), points };
}

export function formatEvalLabel(ev) {
  if (!ev) return '—';
  if (ev.mate != null) {
    return ev.mate > 0 ? `M${ev.mate}` : `-M${Math.abs(ev.mate)}`;
  }
  if (ev.scoreCp == null) return '—';
  const pawns = (ev.scoreCp / 100).toFixed(1);
  return ev.scoreCp > 0 ? `+${pawns}` : pawns;
}

export function evalBarPct(ev) {
  if (!ev) return 50;
  if (ev.mate != null) return ev.mate > 0 ? 97 : 3;
  const cp = Math.max(-900, Math.min(900, ev.scoreCp || 0));
  return 50 + (cp / 900) * 46;
}

/** Build accuracy summary + annotate byPly map in place. */
export function summarizeAnalysis(byPly, verboseMoves) {
  let whitePts = 0;
  let blackPts = 0;
  let whiteMoves = 0;
  let blackMoves = 0;
  const qualities = {};
  for (let ply = 1; ply <= verboseMoves.length; ply++) {
    const before = byPly[ply - 1];
    const after = byPly[ply];
    if (!before || !after) continue;
    const moverIsWhite = ply % 2 === 1;
    const cls = classifyMove(before, after, moverIsWhite);
    const played = verboseMoves[ply - 1];
    byPly[ply] = {
      ...after,
      ...cls,
      playedUci: played ? `${played.from}${played.to}${played.promotion || ''}` : after.playedUci,
    };
    qualities[ply] = cls.quality;
    if (moverIsWhite) {
      whitePts += cls.points;
      whiteMoves += 1;
    } else {
      blackPts += cls.points;
      blackMoves += 1;
    }
  }
  return {
    byPly,
    qualities,
    summary: {
      whiteAccuracy: whiteMoves ? Math.round(whitePts / whiteMoves) : null,
      blackAccuracy: blackMoves ? Math.round(blackPts / blackMoves) : null,
    },
  };
}

/** Map server anti-cheat CPL / matchRank into board quality labels. */
export function qualityFromCpLoss(cpLoss, matchRank) {
  if (cpLoss == null && matchRank == null) return null;
  if (matchRank === 1 && (cpLoss == null || cpLoss <= 20)) return 'best';
  const loss = Number(cpLoss);
  if (!Number.isFinite(loss)) return matchRank === 1 ? 'best' : null;
  if (loss > 200) return 'blunder';
  if (loss > 100) return 'mistake';
  if (loss > 50) return 'inaccuracy';
  if (loss > 20) return 'good';
  return 'best';
}

/**
 * Hydrate club review from game.moves that already have server engine fields
 * (anti-cheat after finished human games). Returns null if nothing to show.
 */
export function serverMovesToClubReview(moves = []) {
  const qualities = {};
  const byPly = {};
  let hasAny = false;
  let whiteLoss = 0;
  let blackLoss = 0;
  let whiteN = 0;
  let blackN = 0;
  (moves || []).forEach((m) => {
    const ply = Number(m?.ply);
    if (!Number.isFinite(ply) || ply < 1) return;
    if (m.cpLoss == null && m.bestUci == null && m.matchRank == null) return;
    hasAny = true;
    const q = qualityFromCpLoss(m.cpLoss, m.matchRank);
    if (q) qualities[ply] = q;
    byPly[ply] = {
      bestMove: m.bestUci || null,
      quality: q,
      loss: m.cpLoss,
      playedUci: m.uci,
      matchRank: m.matchRank,
    };
    if (m.cpLoss != null && Number.isFinite(Number(m.cpLoss))) {
      if (ply % 2 === 1) {
        whiteLoss += Number(m.cpLoss);
        whiteN += 1;
      } else {
        blackLoss += Number(m.cpLoss);
        blackN += 1;
      }
    }
  });
  if (!hasAny) return null;
  const acc = (loss, n) => (n ? Math.round(Math.max(0, Math.min(100, 100 - (loss / n) / 2))) : null);
  return {
    byPly,
    qualities,
    summary: {
      whiteAccuracy: acc(whiteLoss, whiteN),
      blackAccuracy: acc(blackLoss, blackN),
      whiteAvgCpLoss: whiteN ? Math.round(whiteLoss / whiteN) : null,
      blackAvgCpLoss: blackN ? Math.round(blackLoss / blackN) : null,
      source: 'club',
    },
  };
}

/** Blunder → mistake → inaccuracy plies ascending (for scrubber). */
export function mistakePliesFromQualities(qualitiesByPly, { onlyColor } = {}) {
  const bad = new Set(['blunder', 'mistake', 'inaccuracy']);
  return Object.entries(qualitiesByPly || {})
    .filter(([, q]) => bad.has(q))
    .map(([p]) => Number(p))
    .filter((ply) => {
      if (!Number.isFinite(ply)) return false;
      if (onlyColor === 'w') return ply % 2 === 1;
      if (onlyColor === 'b') return ply % 2 === 0;
      return true;
    })
    .sort((a, b) => a - b);
}

/** Map server CloudAnalysisDto → byPly / qualities / summary used by the board UI. */
export function cloudDtoToAnalysisState(dto) {
  const byPly = {};
  const qualities = {};
  (dto?.plies || []).forEach((p) => {
    const ply = p.ply ?? 0;
    byPly[ply] = {
      bestMove: p.bestUci,
      scoreCp: p.scoreCp,
      mate: p.mate,
      quality: p.quality,
      loss: p.lossCp,
      points: p.accuracyPoints,
      playedUci: p.playedUci,
      lines: (p.pvUci || []).map((uci) => ({ uci, pv: [uci] })),
    };
    if (p.quality && ply > 0) qualities[ply] = p.quality;
  });
  return {
    byPly,
    qualities,
    summary: {
      whiteAccuracy: dto?.whiteAccuracy ?? null,
      blackAccuracy: dto?.blackAccuracy ?? null,
      whiteAvgCpLoss: dto?.whiteAvgCpLoss ?? null,
      blackAvgCpLoss: dto?.blackAvgCpLoss ?? null,
      depth: dto?.depth,
      source: 'cloud',
    },
  };
}

/** Tiny SVG path for white-POV eval across plies (0..n). */
export function evalSparklinePath(byPly, maxPly, width = 160, height = 36) {
  if (!byPly || maxPly < 1) return '';
  const pts = [];
  for (let ply = 0; ply <= maxPly; ply++) {
    const ev = byPly[ply];
    if (!ev) continue;
    const cp = Math.max(-600, Math.min(600, evalCp(ev)));
    const x = (ply / maxPly) * width;
    const y = height / 2 - (cp / 600) * (height / 2 - 2);
    pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  if (pts.length < 2) return '';
  return `M ${pts.join(' L ')}`;
}

/** Think-time bars for fair-play (normalized 0–1). */
export function thinkTimeBars(moves, capMs = 8000) {
  return (moves || []).map((m) => {
    const ms = m.thinkTimeMs;
    if (ms == null) return { ply: m.ply, ratio: 0, fast: false };
    return {
      ply: m.ply,
      ratio: Math.min(1, ms / capMs),
      fast: ms < 400 && m.ply > 8,
      ms,
    };
  });
}
